import React, { useState, useEffect } from "react";
import moment from "moment";
import "./ProjectOverviewTab.css";
import { IoIosInformationCircle } from "react-icons/io";
import { MdOutlineAccessTimeFilled } from "react-icons/md";
import { MdEvent } from "react-icons/md";
import { MdGroup } from "react-icons/md";
import { IoMdPerson } from "react-icons/io";
import { MdLabel } from "react-icons/md";
import { MdOutlineCorporateFare } from "react-icons/md";
import { MdOutlinePayments } from "react-icons/md";
import { MdSubtitles } from "react-icons/md";
import { MdDescription } from "react-icons/md";
import { BiSolidComment } from "react-icons/bi";
import { getProjectTaskSummary } from "../../Api/ProjectMaster"; // Adjust import path as needed

const ProjectOverviewTab = ({
  project,
  isEditing,
  saving,
  hasEditPermission,
  handleFieldChange,
  handleSave,
  handleCancel,
  setIsEditing,
  customers,
  customersLoading,
  selectedCustomerName,
  handleCustomerChange,
  projectTypesList,
  loadingProjectTypes,
  handleProjectTypeChangeForStatus,
  getProjectTypeDisplayName,
  projectStatusesList,
  loadingProjectStatuses,
  handleProjectStatusChangeForSubStatus,
  getProjectStatusDisplayName,
  projectSubStatusesList,
  loadingProjectSubStatuses,
  handleProjectSubStatusChange,
  getProjectSubStatusDisplayName,
  resourcesByOrg,
  resourcesByOrgLoading,
  getResourceNameById,
  teams,
  teamData,
  loadingManager,
  loadingSalesperson,
  getStatusBadge,
}) => {
  const [localTags, setLocalTags] = useState(() => {
    if (project?.prTags) {
      return project.prTags.split(",").map((tag, index) => ({
        id: index.toString(),
        text: tag.trim(),
      }));
    }
    return [];
  });

  // State for task-based progress
  const [taskProgress, setTaskProgress] = useState(null);
  const [loadingTaskProgress, setLoadingTaskProgress] = useState(false);
  const [taskProgressError, setTaskProgressError] = useState(null);

  // Fetch task progress when project is loaded
  useEffect(() => {
    const fetchTaskProgress = async () => {
      if (!project?.projectId || !project?.orgId) {
        return;
      }

      setLoadingTaskProgress(true);
      setTaskProgressError(null);

      try {
        const result = await getProjectTaskSummary(project.orgId, project.projectId);
        setTaskProgress(result);
      } catch (error) {
        console.error("Error fetching task progress:", error);
        setTaskProgressError(error.message || "Failed to load task progress");
      } finally {
        setLoadingTaskProgress(false);
      }
    };

    fetchTaskProgress();
  }, [project?.projectId, project?.orgId]);

  const handleAddTag = () => {
    const newTag = {
      id: Date.now().toString(),
      text: "New Tag",
    };
    setLocalTags([...localTags, newTag]);
    const updatedTags = [...localTags, newTag].map((t) => t.text).join(",");
    handleFieldChange("prTags", updatedTags);
  };

  const handleRemoveTag = (id) => {
    const updatedTags = localTags.filter((tag) => tag.id !== id);
    setLocalTags(updatedTags);
    const tagsString = updatedTags.map((t) => t.text).join(",");
    handleFieldChange("prTags", tagsString);
  };

  const handleUpdateTag = (id, text) => {
    const updatedTags = localTags.map((tag) =>
      tag.id === id ? { ...tag, text } : tag,
    );
    setLocalTags(updatedTags);
    const tagsString = updatedTags.map((t) => t.text).join(",");
    handleFieldChange("prTags", tagsString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateDuration = () => {
    if (project?.prStartDate && project?.prEndDate) {
      return moment(project.prEndDate).diff(
        moment(project.prStartDate),
        "days",
      );
    }
    return 0;
  };

  const calculateDaysRemaining = () => {
    if (project?.prEndDate) {
      return Math.max(0, moment(project.prEndDate).diff(moment(), "days"));
    }
    return 0;
  };

  // Time-based progress calculation (fallback)
  const getTimeBasedProgressPercent = () => {
    const total = calculateDuration();
    const remaining = calculateDaysRemaining();
    if (total === 0) return 0;
    const completed = total - remaining;
    return Math.round((completed / total) * 100);
  };

  // Get the progress percentage to display
  const getDisplayProgressPercent = () => {
    // If task progress is available and has valid data, use it
    if (taskProgress && typeof taskProgress.progressPercentage === 'number') {
      return Math.round(taskProgress.progressPercentage);
    }
    // Fallback to time-based progress
    return getTimeBasedProgressPercent();
  };

  // Get the progress value for the progress bar width
  const getProgressBarWidth = () => {
    if (taskProgress && typeof taskProgress.progressPercentage === 'number') {
      return Math.min(taskProgress.progressPercentage, 100);
    }
    return getTimeBasedProgressPercent();
  };

  // Get task counts for display
  const getTaskCounts = () => {
    if (taskProgress) {
      return {
        total: taskProgress.totalTasks || 0,
        completed: taskProgress.completedTasks || 0,
        closed: taskProgress.closedTasks || 0,
        open: taskProgress.openTasks || 0
      };
    }
    return null;
  };

  const taskCounts = getTaskCounts();

  return (
    <div className="potMainContainer">
      <div className="potContentWrapper">
        <div className="potGrid">
          {/* Basic Information Card */}
          <div className={`potCard potBasicInfoCard`}>
            <div className="potCardHeader">
              <div className="potCardHeaderIcon">
                <span className="material-symbols-outlined">
                  <IoIosInformationCircle />
                </span>
              </div>
              <h2 className="potCardHeaderTitle">Basic Info</h2>
            </div>
            <div className="potCardContent">
              <div className="potContentSpace">
                {/* Project ID */}
                <div className="potFieldRow">
                  <span className="potFieldLabel">Project ID</span>
                  <span className="potPidValue">
                    {project?.projectId || "-"}
                  </span>
                </div>

                {/* Client */}
                <div className="potFieldRow">
                  <span className="potFieldLabel">Client</span>
                  {hasEditPermission() && isEditing ? (
                    customersLoading ? (
                      <span className="potFieldValue">Loading...</span>
                    ) : (
                      <select
                        value={project?.prClient || ""}
                        onChange={(e) => handleCustomerChange(e.target.value)}
                        className="potEditableSelect"
                      >
                        <option value="">Select a client</option>
                        {customers.map((customer) => (
                          <option
                            key={customer.customerCode}
                            value={customer.customerCode}
                          >
                            {customer.name} ({customer.customerCode})
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <span
                      className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
                      onClick={() => hasEditPermission() && setIsEditing(true)}
                    >
                      {selectedCustomerName ||
                        project?.prClient ||
                        "Not selected"}
                    </span>
                  )}
                </div>

                {/* Project Type */}
                <div className="potFieldRow">
                  <span className="potFieldLabel">Project Type</span>
                  {hasEditPermission() && isEditing ? (
                    loadingProjectTypes ? (
                      <span className="potFieldValue">Loading...</span>
                    ) : (
                      <select
                        value={project?.prTypes || ""}
                        onChange={(e) =>
                          handleProjectTypeChangeForStatus(e.target.value)
                        }
                        className="potEditableSelect"
                      >
                        <option value="">Select Project Type</option>
                        {projectTypesList.map((type) => (
                          <option
                            key={type.projectTypeGUID}
                            value={type.projectTypeGUID}
                          >
                            {type.projectTypeDesc}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <span
                      className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
                      onClick={() => hasEditPermission() && setIsEditing(true)}
                    >
                      {getProjectTypeDisplayName(project?.prTypes) || "-"}
                    </span>
                  )}
                </div>

                {/* Category */}
                <div className="potFieldRow">
                  <span className="potFieldLabel">Category</span>
                  {hasEditPermission() && isEditing ? (
                    <select
                      value={project?.projectCategory || ""}
                      onChange={(e) =>
                        handleFieldChange("projectCategory", e.target.value)
                      }
                      className="potEditableSelect"
                    >
                      <option value="">Select Category</option>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </select>
                  ) : (
                    <span
                      className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
                      onClick={() => hasEditPermission() && setIsEditing(true)}
                    >
                      {project?.projectCategory || "-"}
                    </span>
                  )}
                </div>

                {/* Priority */}
                <div className="potFieldRow">
                  <span className="potFieldLabel">Priority</span>
                  {hasEditPermission() && isEditing ? (
                    <select
                      value={project?.projectPriority || ""}
                      onChange={(e) =>
                        handleFieldChange("projectPriority", e.target.value)
                      }
                      className="potEditableSelect"
                    >
                      <option value="">Select Priority</option>
                      <option value="Very High">Very High</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  ) : (
                    <span
                      className={`potPriorityBadge ${hasEditPermission() ? "potEditableField" : ""}`}
                      onClick={() => hasEditPermission() && setIsEditing(true)}
                      style={{
                        cursor: hasEditPermission() ? "pointer" : "default",
                      }}
                    >
                      {project?.projectPriority || "No Priority"}
                    </span>
                  )}
                </div>

                {/* Tags Section */}
                <div className="potTagsSectionBorder">
                  <div className="potTagsHeader">
                    <span className="potTagsLabel">Tags</span>
                    {hasEditPermission() && isEditing && (
                      <button
                        className="potAddTagButton"
                        onClick={handleAddTag}
                      >
                        + Add
                      </button>
                    )}
                  </div>
                  <div className="potTagsContainer">
                    {localTags.length > 0 ? (
                      localTags.map((tag) => (
                        <div key={tag.id} className="potTag">
                          {hasEditPermission() && isEditing ? (
                            <span
                              className="potTagText"
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) =>
                                handleUpdateTag(
                                  tag.id,
                                  e.currentTarget.textContent || "",
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                            >
                              {tag.text}
                            </span>
                          ) : (
                            <span className="potTagText">{tag.text}</span>
                          )}
                          {hasEditPermission() && isEditing && (
                            <span
                              className={`potTagRemove material-symbols-outlined`}
                              onClick={() => handleRemoveTag(tag.id)}
                            >
                              close
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="potNoTags">No tags</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side Wrapper */}
          <div className="potRightWrapper">
            {/* Progress and Timeline Row */}
            <div className="potProgressTimelineRow">
              {/* Progress Card */}
              <div className={`potCard potProgressCard`}>
                <div className="potCardHeader">
                  <div className="potCardHeaderIcon">
                    <span className="material-symbols-outlined">
                      <MdOutlineAccessTimeFilled />
                    </span>
                  </div>
                  <h2 className="potCardHeaderTitle">Time Elapsed</h2>
                </div>
                <div className="potProgressGlowBg"></div>
                <div className="potProgressCardContent">
                  <div>
                    <div className="potProgressHeader">
                      <div>
                        <p className="potStatusLabel">Project Progress</p>
                        <h3 className="potStatusValue">
                          {loadingTaskProgress ? "Loading..." : `${getDisplayProgressPercent()}%`}
                        </h3>
                        {/* Show task-based progress indicator */}
                        {taskProgress && !loadingTaskProgress && (
                          <p className="potProgressNote">Based on task completion</p>
                        )}
                      </div>
                      {/* Task Stats Summary */}
                      {taskCounts && taskCounts.total > 0 && (
                        <div className="potTaskStats">
                          <div className="potTaskStat">
                            <span className="potTaskStatLabel">Tasks:</span>
                            <span className="potTaskStatValue">{taskCounts.total}</span>
                          </div>
                          <div className="potTaskStat">
                            <span className="potTaskStatLabel">Done:</span>
                            <span className="potTaskStatValue completed">
                              {taskCounts.completed + taskCounts.closed}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="potProgressBar">
                      <div
                        className="potProgressFill"
                        style={{ width: `${getProgressBarWidth()}%` }}
                      ></div>
                    </div>
                    {/* Progress type indicator */}
                    {taskProgress && !loadingTaskProgress && (
                      <div className="potProgressType">
                        <span className="potProgressTypeBadge task-based">
                          Task-based: {taskProgress.progressPercentage}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="potStatsGrid">
                    <div className="potStatBlock">
                      <p className="potStatLabel">Total Duration</p>
                      <p className="potStatValue">{calculateDuration()} Days</p>
                    </div>
                    <div className="potStatBlock">
                      <p className="potStatLabel">Days Remaining</p>
                      <p className="potStatValueSecondary">
                        {calculateDaysRemaining()} Days
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline Card */}
              <div className="potCard">
                <div className="potCardHeader">
                  <div className="potCardHeaderIcon">
                    <span className="material-symbols-outlined">
                      <MdEvent />
                    </span>
                  </div>
                  <h2 className="potCardHeaderTitle">Timeline</h2>
                </div>
                <div className="potTimelineContent">
                  <div className="potTimelineTrack">
                    <div className="potTimelineColumn">
                      <div className="potTimelineNode"></div>
                      <div className="potTimelineConnector"></div>
                      <div className="potTimelineNodeEmpty"></div>
                    </div>
                    <div className="potTimelineInfo">
                      <div className="potTimelineDate">
                        <p className="potTimeDateLabel">Start Date</p>
                        {hasEditPermission() && isEditing ? (
                          <input
                            type="date"
                            value={
                              project?.prStartDate
                                ? moment(project.prStartDate).format("YYYY-MM-DD")
                                : ""
                            }
                            onChange={(e) =>
                              handleFieldChange("prStartDate", e.target.value)
                            }
                            className="potEditableInput"
                          />
                        ) : (
                          <p
                            className={`potTimeDateValue ${hasEditPermission() ? "potEditableField" : ""}`}
                            onClick={() =>
                              hasEditPermission() && setIsEditing(true)
                            }
                          >
                            {formatDate(project?.prStartDate)}
                          </p>
                        )}
                      </div>
                      <div className="potTimelineDate">
                        <p className="potTimeDateLabel">Target Completion</p>
                        {hasEditPermission() && isEditing ? (
                          <input
                            type="date"
                            value={
                              project?.prEndDate
                                ? moment(project.prEndDate).format("YYYY-MM-DD")
                                : ""
                            }
                            onChange={(e) =>
                              handleFieldChange("prEndDate", e.target.value)
                            }
                            className="potEditableInput"
                          />
                        ) : (
                          <p
                            className={`potTimeDateValue ${hasEditPermission() ? "potEditableField" : ""}`}
                            onClick={() =>
                              hasEditPermission() && setIsEditing(true)
                            }
                          >
                            {formatDate(project?.prEndDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team & Contacts Card */}
            <div className={`potCard potTeamCard`}>
              <div className={`potCardHeader potTeamHeader`}>
                <div className="potTeamHeaderLeft">
                  <div className="potCardHeaderIcon">
                    <span className="material-symbols-outlined">
                      <MdGroup />
                    </span>
                  </div>
                  <h2 className="potCardHeaderTitle">Team &amp; Contact</h2>
                </div>
                {hasEditPermission() && isEditing ? (
                  <span className="potStatusBadge potEditingModeBadge">
                    Editing Mode
                  </span>
                ) : (
                  <span
                    className={`potStatusBadge ${getStatusBadge(project?.projectStatus)}`}
                  >
                    {getProjectStatusDisplayName(project?.projectStatus)}
                  </span>
                )}
              </div>
              <div className="potTeamContent">
                {/* Project Manager */}
                <div className="potTeamMember">
                  <div className="potMemberIcon">
                    <span className="material-symbols-outlined">
                      <IoMdPerson />
                    </span>
                  </div>
                  <div className="potMemberInfo">
                    <p className="potMemberRole">Project Manager</p>
                    {hasEditPermission() && isEditing ? (
                      resourcesByOrgLoading ? (
                        <p className="potMemberName">Loading...</p>
                      ) : (
                        <select
                          value={project?.currentProjectManager || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "currentProjectManager",
                              e.target.value,
                            )
                          }
                          className="potEditableSelect"
                        >
                          <option value="">Select Project Manager</option>
                          {resourcesByOrg.map((resource) => (
                            <option
                              key={resource.resourceId}
                              value={resource.resourceId}
                            >
                              {resource.name} ({resource.resourceId})
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <p
                        className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
                        onClick={() =>
                          hasEditPermission() && setIsEditing(true)
                        }
                      >
                        {loadingManager
                          ? "Loading..."
                          : getResourceNameById(
                              project?.currentProjectManager,
                            ) || "Not assigned"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Team Assigned */}
                <div className="potTeamMember">
                  <div className="potMemberIcon">
                    <span className="material-symbols-outlined">
                      <MdOutlineCorporateFare />
                    </span>
                  </div>
                  <div className="potMemberInfo">
                    <p className="potMemberRole">Team Assigned</p>
                    {hasEditPermission() && isEditing ? (
                      <select
                        value={project?.teamId || ""}
                        onChange={(e) => {
                          const selectedTeam = teams.find(
                            (team) => team.teamId === e.target.value,
                          );
                          handleFieldChange("teamId", e.target.value);
                          handleFieldChange(
                            "teamAssigned",
                            selectedTeam ? selectedTeam.teamName : "",
                          );
                        }}
                        className="potEditableSelect"
                      >
                        <option value="">Select a team</option>
                        {teams.map((team) => (
                          <option key={team.teamId} value={team.teamId}>
                            {team.teamName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p
                        className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
                        onClick={() =>
                          hasEditPermission() && setIsEditing(true)
                        }
                      >
                        {teamData?.teamName ||
                          project?.teamAssigned ||
                          "Not assigned"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Salesperson */}
                <div className="potTeamMember">
                  <div className="potMemberIcon">
                    <span className="material-symbols-outlined">
                      <MdOutlinePayments />
                    </span>
                  </div>
                  <div className="potMemberInfo">
                    <p className="potMemberRole">Salesperson</p>
                    {hasEditPermission() && isEditing ? (
                      resourcesByOrgLoading ? (
                        <p className="potMemberName">Loading...</p>
                      ) : (
                        <select
                          value={project?.salespersonCode || ""}
                          onChange={(e) =>
                            handleFieldChange("salespersonCode", e.target.value)
                          }
                          className="potEditableSelect"
                        >
                          <option value="">Select Salesperson</option>
                          {resourcesByOrg.map((resource) => (
                            <option
                              key={resource.resourceId}
                              value={resource.resourceId}
                            >
                              {resource.name} ({resource.resourceId})
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <p
                        className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
                        onClick={() =>
                          hasEditPermission() && setIsEditing(true)
                        }
                      >
                        {loadingSalesperson
                          ? "Loading..."
                          : getResourceNameById(project?.salespersonCode) ||
                            "Not assigned"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="potTeamMember">
                  <div className="potMemberIcon">
                    <span className="material-symbols-outlined">
                      <MdLabel />
                    </span>
                  </div>
                  <div className="potMemberInfo">
                    <p className="potMemberRole">Status</p>
                    {hasEditPermission() && isEditing ? (
                      loadingProjectStatuses ? (
                        <p className="potMemberName">Loading...</p>
                      ) : (
                        <select
                          value={project?.projectStatus || ""}
                          onChange={(e) =>
                            handleProjectStatusChangeForSubStatus(
                              e.target.value,
                            )
                          }
                          className="potEditableSelect"
                          disabled={!project?.prTypes}
                        >
                          <option value="">
                            {!project?.prTypes
                              ? "Select Project Type first"
                              : "Select Status"}
                          </option>
                          {projectStatusesList.map((status) => (
                            <option
                              key={status.projectStatusGUID}
                              value={status.projectStatusGUID}
                            >
                              {status.projectStatusDesc}
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <p
                        className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
                        onClick={() =>
                          hasEditPermission() && setIsEditing(true)
                        }
                      >
                        <span
                          className={`potStatusBadge ${getStatusBadge(project?.projectStatus)}`}
                        >
                          {getProjectStatusDisplayName(project?.projectStatus)}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Sub Status */}
                <div className="potTeamMember">
                  <div className="potMemberIcon">
                    <span className="material-symbols-outlined">
                      <MdSubtitles />
                    </span>
                  </div>
                  <div className="potMemberInfo">
                    <p className="potMemberRole">Sub Status</p>
                    {hasEditPermission() && isEditing ? (
                      loadingProjectSubStatuses ? (
                        <p className="potMemberName">Loading...</p>
                      ) : (
                        <select
                          value={project?.projectSubStatus || ""}
                          onChange={(e) =>
                            handleProjectSubStatusChange(e.target.value)
                          }
                          className="potEditableSelect"
                          disabled={!project?.projectStatus}
                        >
                          <option value="">
                            {!project?.projectStatus
                              ? "Select Status first"
                              : "Select Sub Status"}
                          </option>
                          {projectSubStatusesList.map((subStatus) => (
                            <option
                              key={subStatus.projectSubStatusGUID}
                              value={subStatus.projectSubStatusGUID}
                            >
                              {subStatus.projectSubStatusDesc}
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <p
                        className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
                        onClick={() =>
                          hasEditPermission() && setIsEditing(true)
                        }
                      >
                        {getProjectSubStatusDisplayName(
                          project?.projectSubStatus,
                        ) || "-"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info / Notes */}
          <div className={`potCard potAdditionalInfoCard`}>
            <div className="potCardHeader">
              <div className="potCardHeaderIcon">
                <span className="material-symbols-outlined">
                  <MdDescription />
                </span>
              </div>
              <h2 className="potCardHeaderTitle">Additional Info</h2>
            </div>
            <div className="potCardContent">
              <div className="potNoteCard">
                <div className="potNoteIcon">
                  <span className="material-symbols-outlined">
                    <BiSolidComment />
                  </span>
                </div>
                <div className="potNoteContent">
                  <div className="potNoteHeader">
                    <h3 className="potNoteTitle">Comments</h3>
                    <span className="potNoteTime">
                      {project?.updatedAt
                        ? moment(project.updatedAt).fromNow()
                        : "Recently"}
                    </span>
                  </div>
                  {hasEditPermission() && isEditing ? (
                    <textarea
                      value={project?.comments || ""}
                      onChange={(e) =>
                        handleFieldChange("comments", e.target.value)
                      }
                      className="potEditableTextarea"
                      rows={3}
                      placeholder="Add comments..."
                    />
                  ) : (
                    <p
                      className={`potNoteText ${hasEditPermission() ? "potEditableField" : ""}`}
                      onClick={() => hasEditPermission() && setIsEditing(true)}
                    >
                      {project?.comments || "No comments"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Controls */}
        {hasEditPermission() && isEditing && (
          <div className="potEditControls">
            <button
              className="potSaveButton"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button
              className="potCancelButton"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectOverviewTab;

// import React, { useState } from "react";
// import moment from "moment";
// import "./ProjectOverviewTab.css";
// import { IoIosInformationCircle } from "react-icons/io";
// import { MdOutlineAccessTimeFilled } from "react-icons/md";
// import { MdEvent } from "react-icons/md";
// import { MdGroup } from "react-icons/md";
// import { IoMdPerson } from "react-icons/io";
// import { MdLabel } from "react-icons/md";
// import { MdOutlineCorporateFare } from "react-icons/md";
// import { MdOutlinePayments } from "react-icons/md";
// import { MdSubtitles } from "react-icons/md";
// import { MdDescription } from "react-icons/md";
// import { BiSolidComment } from "react-icons/bi";

// const ProjectOverviewTab = ({
//   project,
//   isEditing,
//   saving,
//   hasEditPermission,
//   handleFieldChange,
//   handleSave,
//   handleCancel,
//   setIsEditing,
//   customers,
//   customersLoading,
//   selectedCustomerName,
//   handleCustomerChange,
//   projectTypesList,
//   loadingProjectTypes,
//   handleProjectTypeChangeForStatus,
//   getProjectTypeDisplayName,
//   projectStatusesList,
//   loadingProjectStatuses,
//   handleProjectStatusChangeForSubStatus,
//   getProjectStatusDisplayName,
//   projectSubStatusesList,
//   loadingProjectSubStatuses,
//   handleProjectSubStatusChange,
//   getProjectSubStatusDisplayName,
//   resourcesByOrg,
//   resourcesByOrgLoading,
//   getResourceNameById,
//   teams,
//   teamData,
//   loadingManager,
//   loadingSalesperson,
//   getStatusBadge,
// }) => {
//   const [localTags, setLocalTags] = useState(() => {
//     if (project?.prTags) {
//       return project.prTags.split(",").map((tag, index) => ({
//         id: index.toString(),
//         text: tag.trim(),
//       }));
//     }
//     return [];
//   });

//   const handleAddTag = () => {
//     const newTag = {
//       id: Date.now().toString(),
//       text: "New Tag",
//     };
//     setLocalTags([...localTags, newTag]);
//     const updatedTags = [...localTags, newTag].map((t) => t.text).join(",");
//     handleFieldChange("prTags", updatedTags);
//   };

//   const handleRemoveTag = (id) => {
//     const updatedTags = localTags.filter((tag) => tag.id !== id);
//     setLocalTags(updatedTags);
//     const tagsString = updatedTags.map((t) => t.text).join(",");
//     handleFieldChange("prTags", tagsString);
//   };

//   const handleUpdateTag = (id, text) => {
//     const updatedTags = localTags.map((tag) =>
//       tag.id === id ? { ...tag, text } : tag,
//     );
//     setLocalTags(updatedTags);
//     const tagsString = updatedTags.map((t) => t.text).join(",");
//     handleFieldChange("prTags", tagsString);
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "Not set";
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });
//   };

//   const calculateDuration = () => {
//     if (project?.prStartDate && project?.prEndDate) {
//       return moment(project.prEndDate).diff(
//         moment(project.prStartDate),
//         "days",
//       );
//     }
//     return 0;
//   };

//   const calculateDaysRemaining = () => {
//     if (project?.prEndDate) {
//       return Math.max(0, moment(project.prEndDate).diff(moment(), "days"));
//     }
//     return 0;
//   };

//   const getProgressPercent = () => {
//     const total = calculateDuration();
//     const remaining = calculateDaysRemaining();
//     if (total === 0) return 0;
//     const completed = total - remaining;
//     return Math.round((completed / total) * 100);
//   };

//   return (
//     <div className="potMainContainer">
//       <div className="potContentWrapper">
//         <div className="potGrid">
//           {/* Basic Information Card */}
//           <div className={`potCard potBasicInfoCard`}>
//             <div className="potCardHeader">
//               <div className="potCardHeaderIcon">
//                 <span className="material-symbols-outlined">
//                   <IoIosInformationCircle />
//                 </span>
//               </div>
//               <h2 className="potCardHeaderTitle">Basic Info</h2>
//             </div>
//             <div className="potCardContent">
//               <div className="potContentSpace">
//                 {/* Project ID */}
//                 <div className="potFieldRow">
//                   <span className="potFieldLabel">Project ID</span>
//                   <span className="potPidValue">
//                     {project?.projectId || "-"}
//                   </span>
//                 </div>

//                 {/* Client */}
//                 <div className="potFieldRow">
//                   <span className="potFieldLabel">Client</span>
//                   {hasEditPermission() && isEditing ? (
//                     customersLoading ? (
//                       <span className="potFieldValue">Loading...</span>
//                     ) : (
//                       <select
//                         value={project?.prClient || ""}
//                         onChange={(e) => handleCustomerChange(e.target.value)}
//                         className="potEditableSelect"
//                       >
//                         <option value="">Select a client</option>
//                         {customers.map((customer) => (
//                           <option
//                             key={customer.customerCode}
//                             value={customer.customerCode}
//                           >
//                             {customer.name} ({customer.customerCode})
//                           </option>
//                         ))}
//                       </select>
//                     )
//                   ) : (
//                     <span
//                       className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
//                       onClick={() => hasEditPermission() && setIsEditing(true)}
//                     >
//                       {selectedCustomerName ||
//                         project?.prClient ||
//                         "Not selected"}
//                     </span>
//                   )}
//                 </div>

//                 {/* Project Type */}
//                 <div className="potFieldRow">
//                   <span className="potFieldLabel">Project Type</span>
//                   {hasEditPermission() && isEditing ? (
//                     loadingProjectTypes ? (
//                       <span className="potFieldValue">Loading...</span>
//                     ) : (
//                       <select
//                         value={project?.prTypes || ""}
//                         onChange={(e) =>
//                           handleProjectTypeChangeForStatus(e.target.value)
//                         }
//                         className="potEditableSelect"
//                       >
//                         <option value="">Select Project Type</option>
//                         {projectTypesList.map((type) => (
//                           <option
//                             key={type.projectTypeGUID}
//                             value={type.projectTypeGUID}
//                           >
//                             {type.projectTypeDesc}
//                           </option>
//                         ))}
//                       </select>
//                     )
//                   ) : (
//                     <span
//                       className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
//                       onClick={() => hasEditPermission() && setIsEditing(true)}
//                     >
//                       {getProjectTypeDisplayName(project?.prTypes) || "-"}
//                     </span>
//                   )}
//                 </div>

//                 {/* Category */}
//                 <div className="potFieldRow">
//                   <span className="potFieldLabel">Category</span>
//                   {hasEditPermission() && isEditing ? (
//                     <select
//                       value={project?.projectCategory || ""}
//                       onChange={(e) =>
//                         handleFieldChange("projectCategory", e.target.value)
//                       }
//                       className="potEditableSelect"
//                     >
//                       <option value="">Select Category</option>
//                       <option value="Internal">Internal</option>
//                       <option value="External">External</option>
//                     </select>
//                   ) : (
//                     <span
//                       className={`potFieldValue ${hasEditPermission() ? "potEditableField" : ""}`}
//                       onClick={() => hasEditPermission() && setIsEditing(true)}
//                     >
//                       {project?.projectCategory || "-"}
//                     </span>
//                   )}
//                 </div>

//                 {/* Priority */}
//                 <div className="potFieldRow">
//                   <span className="potFieldLabel">Priority</span>
//                   {hasEditPermission() && isEditing ? (
//                     <select
//                       value={project?.projectPriority || ""}
//                       onChange={(e) =>
//                         handleFieldChange("projectPriority", e.target.value)
//                       }
//                       className="potEditableSelect"
//                     >
//                       <option value="">Select Priority</option>
//                       <option value="Very High">Very High</option>
//                       <option value="High">High</option>
//                       <option value="Medium">Medium</option>
//                       <option value="Low">Low</option>
//                     </select>
//                   ) : (
//                     <span
//                       className={`potPriorityBadge ${hasEditPermission() ? "potEditableField" : ""}`}
//                       onClick={() => hasEditPermission() && setIsEditing(true)}
//                       style={{
//                         cursor: hasEditPermission() ? "pointer" : "default",
//                       }}
//                     >
//                       {project?.projectPriority || "No Priority"}
//                     </span>
//                   )}
//                 </div>

//                 {/* Tags Section */}
//                 <div className="potTagsSectionBorder">
//                   <div className="potTagsHeader">
//                     <span className="potTagsLabel">Tags</span>
//                     {hasEditPermission() && isEditing && (
//                       <button
//                         className="potAddTagButton"
//                         onClick={handleAddTag}
//                       >
//                         + Add
//                       </button>
//                     )}
//                   </div>
//                   <div className="potTagsContainer">
//                     {localTags.length > 0 ? (
//                       localTags.map((tag) => (
//                         <div key={tag.id} className="potTag">
//                           {hasEditPermission() && isEditing ? (
//                             <span
//                               className="potTagText"
//                               contentEditable
//                               suppressContentEditableWarning
//                               onBlur={(e) =>
//                                 handleUpdateTag(
//                                   tag.id,
//                                   e.currentTarget.textContent || "",
//                                 )
//                               }
//                               onClick={(e) => e.stopPropagation()}
//                             >
//                               {tag.text}
//                             </span>
//                           ) : (
//                             <span className="potTagText">{tag.text}</span>
//                           )}
//                           {hasEditPermission() && isEditing && (
//                             <span
//                               className={`potTagRemove material-symbols-outlined`}
//                               onClick={() => handleRemoveTag(tag.id)}
//                             >
//                               close
//                             </span>
//                           )}
//                         </div>
//                       ))
//                     ) : (
//                       <span className="potNoTags">No tags</span>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Right Side Wrapper */}
//           <div className="potRightWrapper">
//             {/* Progress and Timeline Row */}
//             <div className="potProgressTimelineRow">
//               {/* Progress Card */}
//               <div className={`potCard potProgressCard`}>
//                 <div className="potCardHeader">
//                   <div className="potCardHeaderIcon">
//                     <span className="material-symbols-outlined">
//                       <MdOutlineAccessTimeFilled />
//                     </span>
//                   </div>
//                   <h2 className="potCardHeaderTitle">Time Elapsed</h2>
//                 </div>
//                 <div className="potProgressGlowBg"></div>
//                 <div className="potProgressCardContent">
//                   <div>
//                     <div className="potProgressHeader">
//                       <div>
//                         <p className="potStatusLabel">Project Progress</p>
//                         <h3 className="potStatusValue">
//                           {getProgressPercent()}%
//                         </h3>
//                       </div>
//                     </div>
//                     <div className="potProgressBar">
//                       <div
//                         className="potProgressFill"
//                         style={{ width: `${getProgressPercent()}%` }}
//                       ></div>
//                     </div>
//                   </div>
//                   <div className="potStatsGrid">
//                     <div className="potStatBlock">
//                       <p className="potStatLabel">Total Duration</p>
//                       <p className="potStatValue">{calculateDuration()} Days</p>
//                     </div>
//                     <div className="potStatBlock">
//                       <p className="potStatLabel">Days Remaining</p>
//                       <p className="potStatValueSecondary">
//                         {calculateDaysRemaining()} Days
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Timeline Card */}
//               <div className="potCard">
//                 <div className="potCardHeader">
//                   <div className="potCardHeaderIcon">
//                     <span className="material-symbols-outlined">
//                       <MdEvent />
//                     </span>
//                   </div>
//                   <h2 className="potCardHeaderTitle">Timeline</h2>
//                 </div>
//                 <div className="potTimelineContent">
//                   <div className="potTimelineTrack">
//                     <div className="potTimelineColumn">
//                       <div className="potTimelineNode"></div>
//                       <div className="potTimelineConnector"></div>
//                       <div className="potTimelineNodeEmpty"></div>
//                     </div>
//                     <div className="potTimelineInfo">
//                       <div className="potTimelineDate">
//                         <p className="potTimeDateLabel">Start Date</p>
//                         {hasEditPermission() && isEditing ? (
//                           <input
//                             type="date"
//                             value={
//                               project?.prStartDate
//                                 ? moment(project.prStartDate).format("YYYY-MM-DD")
//                                 : ""
//                             }
//                             onChange={(e) =>
//                               handleFieldChange("prStartDate", e.target.value)
//                             }
//                             className="potEditableInput"
//                           />
//                         ) : (
//                           <p
//                             className={`potTimeDateValue ${hasEditPermission() ? "potEditableField" : ""}`}
//                             onClick={() =>
//                               hasEditPermission() && setIsEditing(true)
//                             }
//                           >
//                             {formatDate(project?.prStartDate)}
//                           </p>
//                         )}
//                       </div>
//                       <div className="potTimelineDate">
//                         <p className="potTimeDateLabel">Target Completion</p>
//                         {hasEditPermission() && isEditing ? (
//                           <input
//                             type="date"
//                             value={
//                               project?.prEndDate
//                                 ? moment(project.prEndDate).format("YYYY-MM-DD")
//                                 : ""
//                             }
//                             onChange={(e) =>
//                               handleFieldChange("prEndDate", e.target.value)
//                             }
//                             className="potEditableInput"
//                           />
//                         ) : (
//                           <p
//                             className={`potTimeDateValue ${hasEditPermission() ? "potEditableField" : ""}`}
//                             onClick={() =>
//                               hasEditPermission() && setIsEditing(true)
//                             }
//                           >
//                             {formatDate(project?.prEndDate)}
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Team & Contacts Card */}
//             <div className={`potCard potTeamCard`}>
//               <div className={`potCardHeader potTeamHeader`}>
//                 <div className="potTeamHeaderLeft">
//                   <div className="potCardHeaderIcon">
//                     <span className="material-symbols-outlined">
//                       <MdGroup />
//                     </span>
//                   </div>
//                   <h2 className="potCardHeaderTitle">Team &amp; Contact</h2>
//                 </div>
//                 {hasEditPermission() && isEditing ? (
//                   <span className="potStatusBadge potEditingModeBadge">
//                     Editing Mode
//                   </span>
//                 ) : (
//                   <span
//                     className={`potStatusBadge ${getStatusBadge(project?.projectStatus)}`}
//                   >
//                     {getProjectStatusDisplayName(project?.projectStatus)}
//                   </span>
//                 )}
//               </div>
//               <div className="potTeamContent">
//                 {/* Project Manager */}
//                 <div className="potTeamMember">
//                   <div className="potMemberIcon">
//                     <span className="material-symbols-outlined">
//                       <IoMdPerson />
//                     </span>
//                   </div>
//                   <div className="potMemberInfo">
//                     <p className="potMemberRole">Project Manager</p>
//                     {hasEditPermission() && isEditing ? (
//                       resourcesByOrgLoading ? (
//                         <p className="potMemberName">Loading...</p>
//                       ) : (
//                         <select
//                           value={project?.currentProjectManager || ""}
//                           onChange={(e) =>
//                             handleFieldChange(
//                               "currentProjectManager",
//                               e.target.value,
//                             )
//                           }
//                           className="potEditableSelect"
//                         >
//                           <option value="">Select Project Manager</option>
//                           {resourcesByOrg.map((resource) => (
//                             <option
//                               key={resource.resourceId}
//                               value={resource.resourceId}
//                             >
//                               {resource.name} ({resource.resourceId})
//                             </option>
//                           ))}
//                         </select>
//                       )
//                     ) : (
//                       <p
//                         className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
//                         onClick={() =>
//                           hasEditPermission() && setIsEditing(true)
//                         }
//                       >
//                         {loadingManager
//                           ? "Loading..."
//                           : getResourceNameById(
//                               project?.currentProjectManager,
//                             ) || "Not assigned"}
//                       </p>
//                     )}
//                   </div>
//                 </div>

//                 {/* Team Assigned */}
//                 <div className="potTeamMember">
//                   <div className="potMemberIcon">
//                     <span className="material-symbols-outlined">
//                       <MdOutlineCorporateFare />
//                     </span>
//                   </div>
//                   <div className="potMemberInfo">
//                     <p className="potMemberRole">Team Assigned</p>
//                     {hasEditPermission() && isEditing ? (
//                       <select
//                         value={project?.teamId || ""}
//                         onChange={(e) => {
//                           const selectedTeam = teams.find(
//                             (team) => team.teamId === e.target.value,
//                           );
//                           handleFieldChange("teamId", e.target.value);
//                           handleFieldChange(
//                             "teamAssigned",
//                             selectedTeam ? selectedTeam.teamName : "",
//                           );
//                         }}
//                         className="potEditableSelect"
//                       >
//                         <option value="">Select a team</option>
//                         {teams.map((team) => (
//                           <option key={team.teamId} value={team.teamId}>
//                             {team.teamName}
//                           </option>
//                         ))}
//                       </select>
//                     ) : (
//                       <p
//                         className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
//                         onClick={() =>
//                           hasEditPermission() && setIsEditing(true)
//                         }
//                       >
//                         {teamData?.teamName ||
//                           project?.teamAssigned ||
//                           "Not assigned"}
//                       </p>
//                     )}
//                   </div>
//                 </div>

//                 {/* Salesperson */}
//                 <div className="potTeamMember">
//                   <div className="potMemberIcon">
//                     <span className="material-symbols-outlined">
//                       <MdOutlinePayments />
//                     </span>
//                   </div>
//                   <div className="potMemberInfo">
//                     <p className="potMemberRole">Salesperson</p>
//                     {hasEditPermission() && isEditing ? (
//                       resourcesByOrgLoading ? (
//                         <p className="potMemberName">Loading...</p>
//                       ) : (
//                         <select
//                           value={project?.salespersonCode || ""}
//                           onChange={(e) =>
//                             handleFieldChange("salespersonCode", e.target.value)
//                           }
//                           className="potEditableSelect"
//                         >
//                           <option value="">Select Salesperson</option>
//                           {resourcesByOrg.map((resource) => (
//                             <option
//                               key={resource.resourceId}
//                               value={resource.resourceId}
//                             >
//                               {resource.name} ({resource.resourceId})
//                             </option>
//                           ))}
//                         </select>
//                       )
//                     ) : (
//                       <p
//                         className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
//                         onClick={() =>
//                           hasEditPermission() && setIsEditing(true)
//                         }
//                       >
//                         {loadingSalesperson
//                           ? "Loading..."
//                           : getResourceNameById(project?.salespersonCode) ||
//                             "Not assigned"}
//                       </p>
//                     )}
//                   </div>
//                 </div>

//                 {/* Status */}
//                 <div className="potTeamMember">
//                   <div className="potMemberIcon">
//                     <span className="material-symbols-outlined">
//                       <MdLabel />
//                     </span>
//                   </div>
//                   <div className="potMemberInfo">
//                     <p className="potMemberRole">Status</p>
//                     {hasEditPermission() && isEditing ? (
//                       loadingProjectStatuses ? (
//                         <p className="potMemberName">Loading...</p>
//                       ) : (
//                         <select
//                           value={project?.projectStatus || ""}
//                           onChange={(e) =>
//                             handleProjectStatusChangeForSubStatus(
//                               e.target.value,
//                             )
//                           }
//                           className="potEditableSelect"
//                           disabled={!project?.prTypes}
//                         >
//                           <option value="">
//                             {!project?.prTypes
//                               ? "Select Project Type first"
//                               : "Select Status"}
//                           </option>
//                           {projectStatusesList.map((status) => (
//                             <option
//                               key={status.projectStatusGUID}
//                               value={status.projectStatusGUID}
//                             >
//                               {status.projectStatusDesc}
//                             </option>
//                           ))}
//                         </select>
//                       )
//                     ) : (
//                       <p
//                         className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
//                         onClick={() =>
//                           hasEditPermission() && setIsEditing(true)
//                         }
//                       >
//                         <span
//                           className={`potStatusBadge ${getStatusBadge(project?.projectStatus)}`}
//                         >
//                           {getProjectStatusDisplayName(project?.projectStatus)}
//                         </span>
//                       </p>
//                     )}
//                   </div>
//                 </div>

//                 {/* Sub Status */}
//                 <div className="potTeamMember">
//                   <div className="potMemberIcon">
//                     <span className="material-symbols-outlined">
//                       <MdSubtitles />
//                     </span>
//                   </div>
//                   <div className="potMemberInfo">
//                     <p className="potMemberRole">Sub Status</p>
//                     {hasEditPermission() && isEditing ? (
//                       loadingProjectSubStatuses ? (
//                         <p className="potMemberName">Loading...</p>
//                       ) : (
//                         <select
//                           value={project?.projectSubStatus || ""}
//                           onChange={(e) =>
//                             handleProjectSubStatusChange(e.target.value)
//                           }
//                           className="potEditableSelect"
//                           disabled={!project?.projectStatus}
//                         >
//                           <option value="">
//                             {!project?.projectStatus
//                               ? "Select Status first"
//                               : "Select Sub Status"}
//                           </option>
//                           {projectSubStatusesList.map((subStatus) => (
//                             <option
//                               key={subStatus.projectSubStatusGUID}
//                               value={subStatus.projectSubStatusGUID}
//                             >
//                               {subStatus.projectSubStatusDesc}
//                             </option>
//                           ))}
//                         </select>
//                       )
//                     ) : (
//                       <p
//                         className={`potMemberName ${hasEditPermission() ? "potEditableField" : ""}`}
//                         onClick={() =>
//                           hasEditPermission() && setIsEditing(true)
//                         }
//                       >
//                         {getProjectSubStatusDisplayName(
//                           project?.projectSubStatus,
//                         ) || "-"}
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Additional Info / Notes */}
//           <div className={`potCard potAdditionalInfoCard`}>
//             <div className="potCardHeader">
//               <div className="potCardHeaderIcon">
//                 <span className="material-symbols-outlined">
//                   <MdDescription />
//                 </span>
//               </div>
//               <h2 className="potCardHeaderTitle">Additional Info</h2>
//             </div>
//             <div className="potCardContent">
//               <div className="potNoteCard">
//                 <div className="potNoteIcon">
//                   <span className="material-symbols-outlined">
//                     <BiSolidComment />
//                   </span>
//                 </div>
//                 <div className="potNoteContent">
//                   <div className="potNoteHeader">
//                     <h3 className="potNoteTitle">Comments</h3>
//                     <span className="potNoteTime">
//                       {project?.updatedAt
//                         ? moment(project.updatedAt).fromNow()
//                         : "Recently"}
//                     </span>
//                   </div>
//                   {hasEditPermission() && isEditing ? (
//                     <textarea
//                       value={project?.comments || ""}
//                       onChange={(e) =>
//                         handleFieldChange("comments", e.target.value)
//                       }
//                       className="potEditableTextarea"
//                       rows={3}
//                       placeholder="Add comments..."
//                     />
//                   ) : (
//                     <p
//                       className={`potNoteText ${hasEditPermission() ? "potEditableField" : ""}`}
//                       onClick={() => hasEditPermission() && setIsEditing(true)}
//                     >
//                       {project?.comments || "No comments"}
//                     </p>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Edit Controls */}
//         {hasEditPermission() && isEditing && (
//           <div className="potEditControls">
//             <button
//               className="potSaveButton"
//               onClick={handleSave}
//               disabled={saving}
//             >
//               {saving ? "Saving..." : "Save Changes"}
//             </button>
//             <button
//               className="potCancelButton"
//               onClick={handleCancel}
//               disabled={saving}
//             >
//               Cancel
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectOverviewTab;